import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Star, Coins, Check, X, CreditCard } from "lucide-react";

/**
 * ChannelSubscribeButton — per-streamer subscribe/renew/cancel.
 * Drops onto any channel or stream page. Owner sees a neutral status pill.
 * Non-auth users see a "Sign in to subscribe" state.
 */
export default function ChannelSubscribeButton({ ownerUsername }) {
  const { user, refreshMe } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isSelf = user?.username?.toLowerCase() === (ownerUsername || "").toLowerCase();

  const load = async () => {
    if (!user || isSelf) return;
    try {
      const r = await api.get(`/channels/${ownerUsername}/subscribe/status`);
      setStatus(r.data);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, [ownerUsername, user?.username]);

  if (isSelf) {
    return (
      <div className="sc-btn-ghost text-sm inline-flex items-center gap-2 opacity-70 cursor-default" data-testid="sub-btn-self">
        <Star className="w-4 h-4" /> Your channel
      </div>
    );
  }

  if (!user) {
    return (
      <a href="/login" className="sc-btn-ghost text-sm inline-flex items-center gap-2" data-testid="sub-btn-signin">
        <Star className="w-4 h-4 text-[color:var(--sc-volt)]" /> Sign in to subscribe
      </a>
    );
  }

  const price = status?.price_gc ?? 300;
  const goldCoins = user?.gold_coins ?? 0;
  const insufficient = goldCoins < price;

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/channels/${ownerUsername}/subscribe`, {});
      toast.success(`Subscribed to @${ownerUsername} — ${price} GC`);
      if (refreshMe) await refreshMe();
      setConfirmOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Subscribe failed");
    } finally { setBusy(false); }
  };

  const payWithCard = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/channels/${ownerUsername}/subscribe/stripe-checkout`, {
        origin_url: window.location.origin,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;   // hand off to Stripe
      } else {
        toast.error("Stripe: no checkout URL returned");
        setBusy(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stripe checkout failed");
      setBusy(false);
    }
  };

  if (status?.is_subscribed) {
    const exp = status.expires_at ? new Date(status.expires_at) : null;
    const days = exp ? Math.max(0, Math.ceil((exp.getTime() - Date.now()) / 86400000)) : 0;
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-[color:var(--sc-volt)]/40 bg-[color:var(--sc-volt)]/10 text-[color:var(--sc-volt)]" data-testid="sub-btn-active">
        <Check className="w-3.5 h-3.5" />
        <span className="sc-mono text-[10px] tracking-widest uppercase font-bold">Subscribed · {days}d</span>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setConfirmOpen(true)}
        disabled={busy}
        data-testid="sub-btn"
        className="sc-btn text-sm inline-flex items-center gap-2"
        style={{ background: "linear-gradient(135deg, var(--sc-pink), var(--sc-orange))" }}
      >
        <Star className="w-4 h-4" /> Subscribe · {price} GC
      </button>
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="sub-confirm-modal">
          <div className="w-full max-w-sm sc-card p-6 relative">
            <button onClick={() => setConfirmOpen(false)} className="absolute top-3 right-3 text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
            <div className="sc-label" style={{ color: "var(--sc-pink)" }}>Confirm subscription</div>
            <h2 className="sc-heading text-xl font-black mt-1 mb-2">Subscribe to @{ownerUsername}?</h2>
            <p className="text-sm text-zinc-400">30 days of channel perks. You&apos;ll get a Subscriber badge in their chat, priority for mods, and access to Subscribers-only chat if enabled.</p>
            <div className="mt-4 flex items-center justify-between p-3 border border-white/10 rounded-sm bg-black/30">
              <div className="text-xs sc-mono text-zinc-500">Your balance</div>
              <div className="flex items-center gap-1 font-bold"><Coins className="w-4 h-4 text-[color:var(--sc-orange)]" /> {goldCoins.toLocaleString()} GC</div>
            </div>
            <div className="mt-2 flex items-center justify-between p-3 border border-[color:var(--sc-pink)]/40 rounded-sm bg-[color:var(--sc-pink)]/10">
              <div className="text-xs sc-mono text-[color:var(--sc-pink)]">You&apos;ll pay</div>
              <div className="flex items-center gap-1 font-bold text-[color:var(--sc-pink)]"><Coins className="w-4 h-4" /> {price} GC</div>
            </div>
            {insufficient ? (
              <div className="mt-4 text-xs text-[color:var(--sc-pink)] sc-mono" data-testid="sub-btn-insufficient">Need {(price - goldCoins).toLocaleString()} more GC. <a href="/credits" className="underline">Get coins</a></div>
            ) : (
              <button
                onClick={confirm}
                disabled={busy}
                className="mt-4 sc-btn w-full text-sm"
                data-testid="sub-confirm-pay"
                style={{ background: "linear-gradient(135deg, var(--sc-pink), var(--sc-orange))" }}
              >{busy ? "Processing…" : `Pay ${price} GC & Subscribe`}</button>
            )}
            <div className="my-3 flex items-center gap-2 text-[10px] sc-mono uppercase tracking-widest text-zinc-500">
              <span className="flex-1 h-px bg-white/10" /> or <span className="flex-1 h-px bg-white/10" />
            </div>
            <button
              onClick={payWithCard}
              disabled={busy}
              data-testid="sub-confirm-stripe"
              className="sc-btn-ghost w-full text-sm inline-flex items-center justify-center gap-2"
            ><CreditCard className="w-4 h-4" /> Pay with card (USD)</button>
            <div className="text-[10px] text-zinc-500 sc-mono text-center mt-2">
              Secure hosted checkout by Stripe · same 30-day sub
            </div>
          </div>
        </div>
      )}
    </>
  );
}
